import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  CardTitle,
  CardText,
  FormGroup,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import * as localAgenciesActions from '../../store/localAgencies/actions';
import { useDispatch, useSelector } from 'react-redux';
import useClipboard from '../../lib/useClipboard';
import { useTranslation } from 'react-i18next';
import toastr from 'toastr';

const GET_ITALIAN_COUNTRIES_URL =
  process.env.REACT_APP_WS_FUNCTION_URL + '/getItalianCountries';

const loadOptions = async (search, loadedOptions) => {
  if (search.length < 3) return [];

  //?search=&offset=0 || ?search=mi&offset=0
  const response = await fetch(
    `${GET_ITALIAN_COUNTRIES_URL}/?search=${encodeURIComponent(
      search.toLowerCase(),
    )}&offset=${loadedOptions.length}`,
  );
  const data = await response.json();

  return data.map(({ label, zipCode }) => ({
    label,
    value: zipCode,
  }));
};

const getSyncParams = (acquisitionOrder) => {
  switch (acquisitionOrder?.localAgencyFinderId) {
    case 'ibl':
      return {
        brand: 'ibl',
        product: 'RF',
      };
    default:
      return {};
  }
};

const LocalAgencyFinder = ({ lead, acquisitionOrder }) => {
  const [zipCode, setZipCode] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const loading = useSelector((state) => state.LocalAgency.loading);
  const localAgencies = useSelector((state) => state.LocalAgency.localAgencies);

  const filteredAgencies = useCallback(
    (filter) => {
      if (!filter) return localAgencies;
      const lowerFilter = filter.toLowerCase();
      return localAgencies.filter(
        ({ name, displayName, address }) =>
          name.toLowerCase().includes(lowerFilter) ||
          displayName.toLowerCase().includes(lowerFilter) ||
          address.toLowerCase().includes(lowerFilter),
      );
    },
    [localAgencies],
  );

  const [, copyToClipboard] = useClipboard();

  const dispatch = useDispatch();

  // Update zipCode when lead changes
  useEffect(() => {
    if (lead?.zipCode) {
      setZipCode(lead.zipCode);
    }
  }, [lead?.zipCode]);

  const findAgencies = useCallback(
    (zipCode) => {
      const syncParams = getSyncParams(acquisitionOrder);
      if (zipCode) {
        dispatch(
          localAgenciesActions.syncLocalAgencies({
            zipCode,
            ...syncParams,
          }),
        );
      }
    },
    [dispatch, acquisitionOrder],
  );

  useEffect(() => {
    findAgencies(zipCode);
  }, [findAgencies, zipCode]);

  const handleCopyAgencyName = useCallback(
    async (displayName, name) => {
      const copied = await copyToClipboard(name);

      copied
        ? toastr.success(`Agency code copied to clipboard: ${displayName}`)
        : toastr.error(`Unable to copy the agency code: ${displayName}`);
    },
    [copyToClipboard],
  );

  const onChangePlace = ({ value }) => {
    setZipCode(value);
  };

  const { t } = useTranslation();

  const formatOpeningHours = (openingHours) => {
    if (!openingHours || openingHours.length === 0) {
      return 'N/A';
    }

    const dayNames = [
      t('Monday'),
      t('Tuesday'),
      t('Wednesday'),
      t('Thursday'),
      t('Friday'),
      t('Saturday'),
      t('Sunday'),
    ];

    return openingHours
      .map((schedule) => {
        const days = schedule.days.map((day) => dayNames[day]).join(', ');
        const hours = schedule.periods
          .map(
            (period) =>
              // from_hour and to_hour are in the format "HH:MM:SS", we need to slice the last 3 characters to get the minutes and seconds
              // and then convert them to the format "HH:MM"
              `${period.from_hour.slice(0, 5)} - ${period.to_hour.slice(0, 5)}`,
          )
          .join(', ');
        return `${days}: ${hours}`;
      })
      .join('\n');
  };

  return (
    <Card>
      <CardBody>
        {/* Sezione ricerca città */}
        <div className="mb-4">
          <div className="d-flex align-items-center">
            <div className="avatar-sm float-sm-left mr-3">
              <span className="avatar-title rounded-circle bg-info font-size-24">
                <i className="bx bx-map text-light"></i>
              </span>
            </div>
            <CardTitle className="mt-2 mr-3 font-size-18">
              {t('Find for place')}:
            </CardTitle>
            <div className="flex-grow-1">
              <FormGroup className="mb-0">
                <AsyncSelect
                  isMulti={false}
                  isSearchable={true}
                  loadOptions={loadOptions}
                  debounceTimeout={500}
                  loadOptionsOnMenuOpen={false}
                  onChange={onChangePlace}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Sezione filtro e risultati */}
        <div className="mb-2">
          <CardTitle className="font-size-16 mb-2">
            {t('Results for zip code')}:{' '}
            <span className="font-weight-bold">{zipCode || ''}</span>
          </CardTitle>
          <div className="d-flex align-items-center mb-3">
            <CardTitle className="mt-2 mr-3 font-size-16">
              {t('Filter by Agency Name or Address')}:
            </CardTitle>
            <div className="flex-grow-1">
              <FormGroup className="mb-0">
                <input
                  type="text"
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value)}
                  className="form-control"
                  placeholder={t('Type to filter...')}
                />
              </FormGroup>
            </div>
          </div>
        </div>

        {/* Lista risultati */}
        {!zipCode ? (
          <div className="text-center text-muted py-4">
            {t('Please select a place')}
          </div>
        ) : loading ? (
          <div className="text-center text-muted py-4">{t('Loading...')}</div>
        ) : (
          <Row>
            <Col
              md="12"
              className="overflow-auto"
              style={{ maxHeight: 'calc(100vh - 300px)' }}
            >
              {filteredAgencies(selectedFilter).length === 0 ? (
                <div className="text-center text-muted py-4">
                  {loading ? t('Loading...') : t('No agencies found')}
                </div>
              ) : (
                filteredAgencies(selectedFilter).map(
                  ({
                    code,
                    name,
                    displayName,
                    address,
                    streetNumber,
                    phone,
                    email,
                    agencyId,
                    openingHours,
                  }) => (
                    <Card key={code} className="mb-3">
                      <h5 className="card-header border-bottom text-uppercase">
                        {displayName}
                      </h5>
                      <div
                        className="position-absolute"
                        style={{ top: '10px', right: '15px' }}
                      >
                        <span className="badge badge-info font-size-12">
                          {code}
                        </span>
                      </div>
                      <CardBody>
                        <CardTitle className="mt-0">
                          {t('Agency ID')}: {agencyId}
                        </CardTitle>
                        <CardText>
                          <strong>{t('Address')}:</strong> {address}
                          {streetNumber && `, ${streetNumber}`}
                        </CardText>
                        <CardText>
                          <strong>{t('Phone')}:</strong> {phone}
                        </CardText>
                        <CardText>
                          <strong>{t('Email')}:</strong> {email}
                        </CardText>
                        <CardText>
                          <strong>{t('Opening Hours')}:</strong>
                          <pre className="mt-2 p-2 bg-light rounded font-size-12">
                            {formatOpeningHours(openingHours)}
                          </pre>
                        </CardText>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary waves-effect waves-light float-right"
                          onClick={() =>
                            handleCopyAgencyName(displayName, name)
                          }
                        >
                          {t('Copy Agency Name')}
                        </button>
                      </CardBody>
                    </Card>
                  ),
                )
              )}
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default LocalAgencyFinder;
